import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import Modal from '@components/shared/Modal';
import { MELANIE_BONE_IMG } from '@constants';

export default function ScreeningFrequencyModal({ modalOpen, closeModal }: { modalOpen: boolean; closeModal: () => void }) {
  const { t } = useTranslation();

  const screeningHelpQA = [
    {
      question: t('components.screening-frequency-modal.q1'),
      answer: t('components.screening-frequency-modal.a1')
    },
    {
      question: t('components.screening-frequency-modal.q2'),
      answer: t('components.screening-frequency-modal.a2')
    },
    {
      question: t('components.screening-frequency-modal.q3'),
      answer: t('components.screening-frequency-modal.a3')
    },
    {
      question: t('components.screening-frequency-modal.q4'),
      answer: t('components.screening-frequency-modal.a4')
    },
    {
      question: t('components.screening-frequency-modal.q5'),
      answer: t('components.screening-frequency-modal.a5')
    },
    {
      question: t('components.screening-frequency-modal.q6'),
      answer: t('components.screening-frequency-modal.a6')
    },
    {
      question: t('components.screening-frequency-modal.q7'),
      answer: t('components.screening-frequency-modal.a7')
    },
    {
      question: t('components.screening-frequency-modal.q8'),
      answer: t('components.screening-frequency-modal.a8')
    },
    {
      question: t('components.screening-frequency-modal.q9'),
      answer: t('components.screening-frequency-modal.a9')
    }
  ];

  return (
    <Modal title={t('components.screening-frequency-help')} modalOpen={modalOpen} onClose={closeModal} width="670px">
      <DoctorWrap>
        <DoctorImage src={MELANIE_BONE_IMG} alt={t('components.screening-frequency-modal.recommended-doctor-title')} />
        <DoctorText>
          <h6>{t('components.screening-frequency-modal.recommended-doctor-title')}</h6>
          <p dangerouslySetInnerHTML={{ __html: t('components.screening-frequency-modal.recommended-doctor-text') }} />
        </DoctorText>
      </DoctorWrap>
      <QAWrap>
        <Header>
          <HeadingText>{t('components.screening-frequency-modal.are-you')}</HeadingText>
          <HeadingText>{t('components.screening-frequency-modal.recommended-frequency')}</HeadingText>
        </Header>
        {screeningHelpQA.map((item, index) => (
          <QuestionAnswer key={index}>
            <Question>{item.question}</Question>
            <Answer>{item.answer}</Answer>
          </QuestionAnswer>
        ))}
      </QAWrap>
      <ButtonContainer>
        <AltButton onClick={closeModal}>{t('components.close')}</AltButton>
      </ButtonContainer>
    </Modal>
  );
}

const DoctorWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 30px 60px;
  gap: 30px;
  margin-bottom: 40px;
  background: ${Colors.mint50};

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    gap: 20px;
  }
`;

const DoctorImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
`;

const DoctorText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 0;
  gap: 5px;

  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
  }

  h6 {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 700;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${Colors.navyBlue};

    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 140%;
    }
  }

  p {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    color: ${Colors.navyBlue};

    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 150%;
    }
  }
`;

const QAWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0;
  gap: 15px;
  text-align: left;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  gap: 30px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    padding: 0;
  }
`;

const HeadingText = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${Colors.navyBlue};

  &:last-child {
    width: 300px;

    @media (max-width: 768px) {
      width: 100%;
      display: none;
    }
  }
`;

const QuestionAnswer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px;
  gap: 30px;
  border-top: 1px solid ${Colors.ecru};
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    padding: 20px 0 0;
  }
`;

const Question = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 140%;
  color: ${Colors.navyBlue};
  flex: 1;
`;

const Answer = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: ${Colors.navyBlue};
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const AltButton = styled.button`
  display: flex;
  width: auto;
  background: none;
  border: none;
  border-bottom: 2px solid ${Colors.navyBlue};
  color: ${Colors.navyBlue};
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 1px;
  text-align: center;
  cursor: pointer;
  padding: 0 0 2px;
  margin: 30px auto;
`;
